import * as AppCore from '@/utilities/AppCore';
import { VisualFile } from '@/types/visual_file';

export interface Profile {
  profile_id: number,
  name: string,
  description: string,
  visuals: VisualFile[],
  uniqueid: string,
  instagram?: string,
  tiktok?: string,
  snapchat?: string,
  facebook?: string,
  x?: string,
  website?: string,
  optionalfilled?: boolean,
  children_have?: number,
  children_want?: number,
  educationlevel?: number,
  job_name?: string,
  job_title?: string,
  sexualorientation?: number,
  pronouns?: string,
  pets?: number,
  drink?: number,
  smoke?: number,
  weed?: number,
  drugs?: number,
  exercise?: number,
  activeonsocial?: number,
  lookingfor?: number,
  relationshiptype?: number,
  languages?: string,
  interests?: number,
  status?: string,
  quote?: string,
  interests_general?: string,
  interests_music?: string,
  interests_movies?: string,
  interests_games?: string,
  interests_books?: string,
  interests_groups?: string,
  desiredqualities?: number,
  ethnicity?: number,
  politics?: number,
  religion?: number,
}

export const UNKNOWN_PROFILE : Profile = {
  profile_id: -1,
  name: "Unknown",
  description: "Unknown",
  visuals: [],
  uniqueid: "unknown",
};

export enum Ethnicity {
  ETHNICITY_NONE = 0,
  ETHNICITY_WHITE = 1,
  ETHNICITY_BLACK = 2,
  ETHNICITY_EAST_ASIAN = 4,
  ETHNICITY_HISPANIC = 8,
  ETHNICITY_MIDDLE_EASTERN = 16,
  ETHNICITY_NATIVE_AMERICAN = 32,
  ETHNICITY_PACIFIC_ISLANDER = 64,
  ETHNICITY_SOUTH_ASIAN = 128,
  ETHNICITY_SOUTHEAST_ASIAN = 256,
  ETHNICITY_OTHER = 512,
};

export const EthnicityNames : Map<number, string> = new Map([
  [Ethnicity.ETHNICITY_WHITE, "White/Caucasian"],
  [Ethnicity.ETHNICITY_BLACK, "Black/African Descent"],
  [Ethnicity.ETHNICITY_EAST_ASIAN, "East Asian"],
  [Ethnicity.ETHNICITY_HISPANIC, "Hispanic/Latino"],
  [Ethnicity.ETHNICITY_MIDDLE_EASTERN, "Middle Eastern"],
  [Ethnicity.ETHNICITY_NATIVE_AMERICAN, "Native American"],
  [Ethnicity.ETHNICITY_PACIFIC_ISLANDER, "Pacific Islander"],
  [Ethnicity.ETHNICITY_SOUTH_ASIAN, "South Asian"],
  [Ethnicity.ETHNICITY_SOUTHEAST_ASIAN, "Southeast Asian"],
  [Ethnicity.ETHNICITY_OTHER, "Other"],
]);

export function getEthnicities(profile: Profile) : string {
  return AppCore.getEnumValuesJoined(profile.ethnicity, Ethnicity, EthnicityNames);
}

export enum Religion {
  RELIGION_NONE = 0,
  RELIGION_AGNOSTIC = 1,
  RELIGION_ATHEIST = 2,
  RELIGION_BUDDHIST = 4,
  RELIGION_CATHOLIC = 8,
  RELIGION_CHRISTIAN = 16,
  RELIGION_HINDU = 32,
  RELIGION_JEWISH = 64,
  RELIGION_MUSLIM = 128,
  RELIGION_SIKH = 256,
  RELIGION_SPIRITUAL = 512,
  RELIGION_OTHER = 1024,
};

export const ReligionNames : Map<number, string> = new Map([
  [Religion.RELIGION_AGNOSTIC, "Agnostic"],
  [Religion.RELIGION_ATHEIST, "Atheist"],
  [Religion.RELIGION_BUDDHIST, "Buddhist"],
  [Religion.RELIGION_CATHOLIC, "Catholic"],
  [Religion.RELIGION_CHRISTIAN, "Christian"],
  [Religion.RELIGION_HINDU, "Hindu"],
  [Religion.RELIGION_JEWISH, "Jewish"],
  [Religion.RELIGION_MUSLIM, "Muslim"],
  [Religion.RELIGION_SIKH, "Sikh"],
  [Religion.RELIGION_SPIRITUAL, "Spiritual"],
  [Religion.RELIGION_OTHER, "Other"],
]);

export function getReligions(profile: Profile) : string {
  return AppCore.getEnumValuesJoined(profile.religion, Religion, ReligionNames);
}

export enum Pronoun {
  PRONOUN_NONE = 0,
  PRONOUN_SHE = 1,
  PRONOUN_HER = 2,
  PRONOUN_HERS = 4,
  PRONOUN_HE = 8,
  PRONOUN_HIM = 16,
  PRONOUN_HIS = 32,
  PRONOUN_THEY = 64,
  PRONOUN_THEM = 128,
  PRONOUN_THEIRS = 256,
  PRONOUN_VE = 512,
  PRONOUN_VER = 1024,
  PRONOUN_VIS = 2048,
  PRONOUN_ZE = 4096,
  PRONOUN_HIR = 8192,
  PRONOUN_HIRS = 16384,
  PRONOUN_AE = 32768,
  PRONOUN_AER = 65536,
  PRONOUN_AERS = 131072,
  PRONOUN_EY = 262144,
  PRONOUN_EM = 524288,
  PRONOUN_EIRS = 1048576,
  PRONOUN_FAE = 2097152,
  PRONOUN_FAER = 4194304,
  PRONOUN_FAERS = 8388608,
  PRONOUN_XE = 16777216,
  PRONOUN_XEM = 33554432,
  PRONOUN_XYRS = 67108864,
  PRONOUN_ZIR = 134217728,
  PRONOUN_ZIRS = 268435456,
  PRONOUN_NOT_LISTED = 536870912,
};

export const PronounNames : Map<number, string> = new Map([
  [Pronoun.PRONOUN_SHE, "she"],
  [Pronoun.PRONOUN_HER, "her"],
  [Pronoun.PRONOUN_HERS, "hers"],
  [Pronoun.PRONOUN_HE, "he"],
  [Pronoun.PRONOUN_HIM, "him"],
  [Pronoun.PRONOUN_HIS, "his"],
  [Pronoun.PRONOUN_THEY, "they"],
  [Pronoun.PRONOUN_THEM, "them"],
  [Pronoun.PRONOUN_THEIRS, "theirs"],
  [Pronoun.PRONOUN_VE, "ve"],
  [Pronoun.PRONOUN_VER, "ver"],
  [Pronoun.PRONOUN_VIS, "vis"],
  [Pronoun.PRONOUN_ZE, "ze"],
  [Pronoun.PRONOUN_HIR, "hir"],
  [Pronoun.PRONOUN_HIRS, "hirs"],
  [Pronoun.PRONOUN_AE, "ae"],
  [Pronoun.PRONOUN_AER, "aer"],
  [Pronoun.PRONOUN_AERS, "aers"],
  [Pronoun.PRONOUN_EY, "ey"],
  [Pronoun.PRONOUN_EM, "em"],
  [Pronoun.PRONOUN_EIRS, "eirs"],
  [Pronoun.PRONOUN_FAE, "fae"],
  [Pronoun.PRONOUN_FAER, "faer"],
  [Pronoun.PRONOUN_FAERS, "faers"],
  [Pronoun.PRONOUN_XE, "xe"],
  [Pronoun.PRONOUN_XEM, "xem"],
  [Pronoun.PRONOUN_XYRS, "xyrs"],
  [Pronoun.PRONOUN_ZIR, "zir"],
  [Pronoun.PRONOUN_ZIRS, "zirs"],
  [Pronoun.PRONOUN_NOT_LISTED, "Not listed"],
]);

export function getPronouns(profile: Profile) : string {
  return AppCore.getEnumValuesJoined(profile.religion, Pronoun, PronounNames);
}

export enum Sexuality {
  SEXUALITY_NONE = 0,
  SEXUALITY_STRAIGHT = 1,
  SEXUALITY_LESBIAN = 2,
  SEXUALITY_BISEXUAL = 4,
  SEXUALITY_ALLOSEXUAL = 8,
  SEXUALITY_ANDROSEXUAL = 16,
  SEXUALITY_ASEXUAL = 32,
  SEXUALITY_AUTOSEXUAL = 64,
  SEXUALITY_BICURIOUS = 128,
  SEXUALITY_DEMISEXUAL = 256,
  SEXUALITY_FLUID = 512,
  SEXUALITY_GRAYSEXUAL = 1024,
  SEXUALITY_GYNESEXUAL = 2048,
  SEXUALITY_MONOSEXUAL = 4096,
  SEXUALITY_OMNISEXUAL = 8192,
  SEXUALITY_PANSEXUAL = 16384,
  SEXUALITY_POLYSEXUAL = 32768,
  SEXUALITY_QUEER = 65536,
  SEXUALITY_QUESTIONING = 131072,
  SEXUALITY_SKOLOSEXUAL = 262144,
  SEXUALITY_SPECTRASEXUAL = 524288,
  SEXUALITY_GAY = 1048576,
  SEXUALITY_NOT_LISTED = 2097152,
};

export const SexualityNames : Map<number, string> = new Map([
  [Sexuality.SEXUALITY_STRAIGHT, "Straight"],
  [Sexuality.SEXUALITY_LESBIAN, "Lesbian"],
  [Sexuality.SEXUALITY_BISEXUAL, "Bisexual"],
  [Sexuality.SEXUALITY_ALLOSEXUAL, "Allosexual"],
  [Sexuality.SEXUALITY_ANDROSEXUAL, "Androsexual"],
  [Sexuality.SEXUALITY_ASEXUAL, "Asexual"],
  [Sexuality.SEXUALITY_AUTOSEXUAL, "Autosexual"],
  [Sexuality.SEXUALITY_BICURIOUS, "Bicurious"],
  [Sexuality.SEXUALITY_DEMISEXUAL, "Demisexual"],
  [Sexuality.SEXUALITY_FLUID, "Fluid"],
  [Sexuality.SEXUALITY_GRAYSEXUAL, "Graysexual"],
  [Sexuality.SEXUALITY_GYNESEXUAL, "Gynesexual"],
  [Sexuality.SEXUALITY_MONOSEXUAL, "Monosexual"],
  [Sexuality.SEXUALITY_OMNISEXUAL, "Omnisexual"],
  [Sexuality.SEXUALITY_PANSEXUAL, "Pansexual"],
  [Sexuality.SEXUALITY_POLYSEXUAL, "Polysexual"],
  [Sexuality.SEXUALITY_QUEER, "Queer"],
  [Sexuality.SEXUALITY_QUESTIONING, "Questioning"],
  [Sexuality.SEXUALITY_SKOLOSEXUAL, "Skolosexual"],
  [Sexuality.SEXUALITY_SPECTRASEXUAL, "Spectrasexual"],
  [Sexuality.SEXUALITY_GAY, "Gay"],
  [Sexuality.SEXUALITY_NOT_LISTED, "Not listed"],
]);

export function getSexualities(profile: Profile) : string {
  return AppCore.getEnumValuesJoined(profile.sexualorientation, Sexuality, SexualityNames);
}

export enum FamilyPlan {
  FAMILY_PLAN_NONE = 0,
  FAMILY_PLAN_WANT_CHILDREN = 1,
  FAMILY_PLAN_OPEN_TO_CHILDREN = 2,
  FAMILY_PLAN_DO_NOT_WANT_CHILDREN = 4,
  FAMILY_PLAN_NOT_SURE_YET = 8,
};

export const FamilyPlanNames : Map<number, string> = new Map([
  [FamilyPlan.FAMILY_PLAN_WANT_CHILDREN, "Want children"],
  [FamilyPlan.FAMILY_PLAN_OPEN_TO_CHILDREN, "Open to children"],
  [FamilyPlan.FAMILY_PLAN_DO_NOT_WANT_CHILDREN, "Don't want children"],
  [FamilyPlan.FAMILY_PLAN_NOT_SURE_YET, "Not sure yet"],
]);

export function getChildrenStatus(profile: Profile) : string {
  return AppCore.getEnumValuesJoined(profile.children_want, FamilyPlan, FamilyPlanNames);
}

export enum Pet {
  PET_NONE = 0,
  PET_DOG = 1,
  PET_CAT = 2,
  PET_BIRD = 4,
  PET_FISH = 8,
  PET_REPTILE = 16,
};

export const PetNames : Map<number, string> = new Map([
  [Pet.PET_DOG, "Dogs"],
  [Pet.PET_CAT, "Cats"],
  [Pet.PET_BIRD, "Birds"],
  [Pet.PET_FISH, "Fish"],
  [Pet.PET_REPTILE, "Reptiles"],
]);

export function getPets(profile: Profile) : string {
  return AppCore.getEnumValuesJoined(profile.children_want, FamilyPlan, FamilyPlanNames);
}
